import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Clock, MapPin, Wrench, CalendarPlus } from "lucide-react";
import { useClientAppointments } from "@/hooks/useClientAppointments";
import { generateGoogleCalendarUrl } from "@/hooks/useAppointments";
import type { Appointment } from "@/hooks/useAppointments";

const STATUS_GROUPS: Record<string, string[]> = {
  all: [],
  pending:   ["proposed", "counterProposed"],
  confirmed: ["accepted", "confirmed"],
  closed:    ["declined", "canceled", "expired"],
};

const statusFilters = [
  { value: "all",       label: "Tous" },
  { value: "pending",   label: "En attente" },
  { value: "confirmed", label: "Confirmés" },
  { value: "closed",    label: "Archivés" },
];

const statusBadge: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  proposed:       { label: "En attente",  variant: "secondary" },
  counterProposed:{ label: "En attente",  variant: "secondary" },
  accepted:       { label: "Confirmé",    variant: "default" },
  confirmed:      { label: "Confirmé",    variant: "default" },
  declined:       { label: "Archivé",     variant: "outline" },
  canceled:       { label: "Archivé",     variant: "outline" },
  expired:        { label: "Archivé",     variant: "outline" },
};

const ClientAppointmentHistory = () => {
  const [filter, setFilter] = useState("all");
  const { data: appointments = [], isLoading } = useClientAppointments(STATUS_GROUPS[filter]);

  const locationLabel = (apt: Appointment) =>
    apt.location_type === "call"
      ? "📞 Appel / Visio"
      : apt.location_type === "clientAddress"
        ? `🏠 ${apt.location_text || "Chez le client"}`
        : `📍 ${apt.location_text}`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          Mes rendez-vous
        </h2>
        <Badge variant="secondary" className="text-xs">
          {appointments.length} RDV
        </Badge>
      </div>

      <div className="flex flex-wrap gap-2">
        {statusFilters.map((f) => (
          <Button
            key={f.value}
            size="sm"
            variant={filter === f.value ? "default" : "outline"}
            className="text-xs h-7 px-3"
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-muted-foreground text-sm">Chargement...</div>
      ) : appointments.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Calendar className="mx-auto h-10 w-10 text-muted-foreground/30" />
            <p className="mt-2 text-sm text-muted-foreground">
              {filter === "all" ? "Aucun rendez-vous pour le moment" : "Aucun rendez-vous avec ce statut"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {appointments.map((apt: any) => {
            const config = statusBadge[apt.status] || statusBadge.proposed;
            const startDate = new Date(apt.starts_at);
            const shortId = `RDV-${apt.id.slice(0, 4).toUpperCase()}`;

            return (
              <Card key={apt.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1.5 flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono text-muted-foreground">{shortId}</span>
                        <Badge variant={config.variant} className="text-[10px]">{config.label}</Badge>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm">
                        <Wrench className="h-3.5 w-3.5 text-primary shrink-0" />
                        <span className="font-medium truncate">{apt.other_name}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {startDate.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {startDate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} · {apt.duration_minutes} min
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {locationLabel(apt)}
                        </span>
                      </div>
                      {apt.notes && (
                        <p className="text-xs italic text-muted-foreground mt-1">{apt.notes}</p>
                      )}
                    </div>

                    {apt.status === "accepted" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[10px] px-2 shrink-0"
                        onClick={() => window.open(generateGoogleCalendarUrl(apt), "_blank")}
                      >
                        <CalendarPlus className="h-3 w-3 mr-1" /> Google
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientAppointmentHistory;
